import {
    FunctionComponent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    useGetVoucherTemplateQuery,
    usePostVoucherTemplateMutation,
    usePutVoucherTemplateMutation,
    VoucherTemplateLanguage,
} from '@starjack/entities/voucherTemplate';
import { useNotification } from '@starjack/features/notifications';
import { useEditorElements } from './hooks/useEditorElements';
import { useVoucherFonts } from './hooks/useVoucherFonts';
import { useVoucherTemplateFiles } from './hooks/useVoucherTemplateFiles';
import { EditorViewport } from './components/EditorViewport';
import { PropertyPanel } from './components/PropertyPanel';
import { PreviewDialog } from './components/PreviewDialog';
import { BackgroundUploadDialog } from './components/BackgroundUploadDialog';
import { FontManagerDialog } from './components/FontManagerDialog';
import { SvgIcon } from '@mui/material';
import { EyeIcon, PhotoIcon, PaintBrushIcon } from '@heroicons/react/24/solid';

type VoucherTemplateEditorContentProps = {
    liftOperatorId?: string;
};

const LANGUAGES: { value: VoucherTemplateLanguage; label: string }[] = [
    { value: 'DE', label: 'Deutsch' },
    { value: 'EN', label: 'Englisch' },
    { value: 'IT', label: 'Italienisch' },
    { value: 'FR', label: 'Französisch' },
];

export const VoucherTemplateEditorContent: FunctionComponent<
    VoucherTemplateEditorContentProps
> = ({ liftOperatorId }) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { addNotification } = useNotification();
    const templateId = searchParams.get('id');

    const isCreateMode = !templateId;

    // API hooks
    const { data: template, isLoading: templateLoading } =
        useGetVoucherTemplateQuery(templateId || '', {
            skip: isCreateMode,
        });

    const fontManager = useVoucherFonts(liftOperatorId);

    const [postTemplate, { isLoading: isCreating }] =
        usePostVoucherTemplateMutation();
    const [putTemplate, { isLoading: isSaving }] =
        usePutVoucherTemplateMutation();

    const { fetchBackground } = useVoucherTemplateFiles();

    // Dialog states
    const [previewOpen, setPreviewOpen] = useState(false);
    const [backgroundUploadOpen, setBackgroundUploadOpen] = useState(false);
    const [fontManagerOpen, setFontManagerOpen] = useState(false);
    const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
    const [backgroundLoading, setBackgroundLoading] = useState(false);
    const backgroundUrlRef = useRef<string | null>(null);

    const setBackgroundUrlSafe = useCallback((url: string | null) => {
        if (backgroundUrlRef.current) {
            URL.revokeObjectURL(backgroundUrlRef.current);
        }
        backgroundUrlRef.current = url;
        setBackgroundUrl(url);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (backgroundUrlRef.current) {
                URL.revokeObjectURL(backgroundUrlRef.current);
            }
        };
    }, []);

    // Meta form state
    const [name, setName] = useState('');
    const [language, setLanguage] = useState<VoucherTemplateLanguage>('DE');
    const [defaultText, setDefaultText] = useState('');
    const [isMetaDirty, setIsMetaDirty] = useState(false);

    // Editor elements state
    const editor = useEditorElements(template?.elements || {});

    // Sync template data when loaded
    useEffect(() => {
        if (template) {
            setName(template.name);
            setLanguage(template.language);
            setDefaultText(template.defaultText);
            setIsMetaDirty(false);
            editor.setElements(template.elements);

            // Load blank background image for the editor canvas
            if (template.file && template.id) {
                setBackgroundLoading(true);
                fetchBackground(template.id).then((url) => {
                    if (url) setBackgroundUrlSafe(url);
                    setBackgroundLoading(false);
                });
            }
        }
    }, [template]);

    const hasUnsavedChanges = editor.isDirty || isMetaDirty;

    // Unsaved changes warning
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () =>
            window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!name.trim()) {
            addNotification({
                type: 'error',
                message: 'Bitte einen Namen eingeben.',
            });
            return false;
        }

        const elements = editor.getBackendElements();

        try {
            if (isCreateMode) {
                const result = await postTemplate({
                    liftOperatorId: liftOperatorId || null,
                    language,
                    name: name.trim(),
                    defaultText,
                    elements,
                }).unwrap();

                addNotification({
                    type: 'success',
                    message: `Vorlage "${name}" erstellt.`,
                });

                // Navigate to edit mode with new ID (replace to avoid back-button issues)
                navigate(`/gutscheinvorlagen/bearbeiten?id=${result.id}`, {
                    replace: true,
                });
            } else {
                await putTemplate({
                    id: templateId!,
                    body: {
                        name: name.trim(),
                        defaultText,
                        language,
                        elements,
                    },
                }).unwrap();

                addNotification({
                    type: 'success',
                    message: `Vorlage "${name}" gespeichert.`,
                });
            }

            editor.resetDirty();
            setIsMetaDirty(false);
            return true;
        } catch {
            addNotification({
                type: 'error',
                message: 'Fehler beim Speichern.',
            });
            return false;
        }
    }, [
        name,
        language,
        defaultText,
        editor,
        isCreateMode,
        templateId,
        liftOperatorId,
        postTemplate,
        putTemplate,
        navigate,
        addNotification,
    ]);

    // The preview is rendered server-side from the persisted template, so any
    // unsaved edits must be flushed first — otherwise the preview shows the
    // last saved state instead of what the user currently sees in the editor.
    // The ref guards against a fast double-click re-entering before the
    // isSaving-driven disabled state has rendered (which would double-save).
    const previewInFlight = useRef(false);
    const handlePreview = useCallback(async () => {
        if (previewInFlight.current) return;
        previewInFlight.current = true;
        try {
            if (hasUnsavedChanges) {
                const saved = await handleSave();
                if (!saved) return;
            }
            setPreviewOpen(true);
        } finally {
            previewInFlight.current = false;
        }
    }, [hasUnsavedChanges, handleSave]);

    const handleBack = useCallback(() => {
        if (
            hasUnsavedChanges &&
            !window.confirm(
                'Es gibt ungespeicherte Änderungen. Soll die Seite wirklich verlassen werden?'
            )
        ) {
            return;
        }
        navigate('/gutscheinvorlagen');
    }, [navigate, hasUnsavedChanges]);

    // Loading state
    if (!isCreateMode && templateLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 400,
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                sx={{ mb: 3 }}
            >
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Button variant='text' onClick={handleBack}>
                        ← Zurück
                    </Button>
                    <Typography variant='h5'>
                        {isCreateMode
                            ? 'Neue Gutscheinvorlage'
                            : `Vorlage bearbeiten: ${template?.name || ''}`}
                    </Typography>
                    {hasUnsavedChanges && (
                        <Typography variant='caption' color='warning.main'>
                            (Ungespeicherte Änderungen)
                        </Typography>
                    )}
                </Stack>

                <Stack direction='row' spacing={1}>
                    {!isCreateMode && (
                        <>
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={
                                    <SvgIcon fontSize='small'>
                                        <PhotoIcon />
                                    </SvgIcon>
                                }
                                onClick={() => setBackgroundUploadOpen(true)}
                            >
                                Hintergrund
                            </Button>
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={
                                    <SvgIcon fontSize='small'>
                                        <PaintBrushIcon />
                                    </SvgIcon>
                                }
                                onClick={() => setFontManagerOpen(true)}
                            >
                                Schriftarten
                            </Button>
                            <Button
                                variant='outlined'
                                size='small'
                                disabled={isCreating || isSaving}
                                startIcon={
                                    <SvgIcon fontSize='small'>
                                        <EyeIcon />
                                    </SvgIcon>
                                }
                                onClick={handlePreview}
                            >
                                Vorschau
                            </Button>
                        </>
                    )}
                    <LoadingButton
                        variant='contained'
                        onClick={handleSave}
                        loading={isCreating || isSaving}
                        disabled={
                            !name.trim() ||
                            isCreating ||
                            isSaving ||
                            (!isCreateMode && !hasUnsavedChanges)
                        }
                    >
                        Speichern
                    </LoadingButton>
                </Stack>
            </Stack>

            {/* Create mode guidance */}
            {isCreateMode && (
                <Alert severity='info' sx={{ mb: 3 }}>
                    Die Vorlage zuerst speichern, um ein Hintergrund-PDF
                    hochzuladen und die Vorschau zu nutzen.
                </Alert>
            )}

            {/* Meta Form */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label='Name'
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setIsMetaDirty(true);
                                }}
                                fullWidth
                                required
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                select
                                label='Sprache'
                                value={language}
                                onChange={(e) => {
                                    setLanguage(
                                        e.target
                                            .value as VoucherTemplateLanguage
                                    );
                                    setIsMetaDirty(true);
                                }}
                                fullWidth
                                size='small'
                            >
                                {LANGUAGES.map((l) => (
                                    <MenuItem key={l.value} value={l.value}>
                                        {l.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label='Standard-Text'
                                value={defaultText}
                                onChange={(e) => {
                                    setDefaultText(e.target.value);
                                    setIsMetaDirty(true);
                                }}
                                fullWidth
                                size='small'
                                multiline
                                maxRows={3}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Editor: Viewport + PropertyPanel */}
            <Grid container spacing={2}>
                {/* Viewport (70%) */}
                <Grid item xs={12} md={8} lg={9}>
                    <Card>
                        <CardContent sx={{ p: 1 }}>
                            <EditorViewport
                                elements={editor.elements}
                                selectedType={editor.selectedType}
                                backgroundUrl={backgroundUrl}
                                backgroundLoading={backgroundLoading}
                                defaultText={defaultText}
                                onSelect={editor.selectElement}
                                onMove={editor.moveElement}
                                onResize={(type, w, h, x, y) => {
                                    editor.resizeElement(type, w, h, x, y);
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Property Panel (30%) */}
                <Grid item xs={12} md={4} lg={3}>
                    <Card sx={{ position: 'sticky', top: 16 }}>
                        <PropertyPanel
                            selectedElement={editor.selectedElement}
                            selectedType={editor.selectedType}
                            availableFonts={fontManager.fonts}
                            onUpdate={editor.updateElement}
                        />
                    </Card>
                </Grid>
            </Grid>

            {/* Dialogs */}
            <PreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                templateId={templateId}
            />

            <BackgroundUploadDialog
                open={backgroundUploadOpen}
                onClose={() => setBackgroundUploadOpen(false)}
                templateId={templateId}
                onSuccess={() => {
                    // Reload the blank background after upload
                    if (templateId) {
                        fetchBackground(templateId).then((url) => {
                            if (url) setBackgroundUrlSafe(url);
                        });
                    }
                }}
            />

            <FontManagerDialog
                open={fontManagerOpen}
                onClose={() => setFontManagerOpen(false)}
                fonts={fontManager.fonts}
                isUploading={fontManager.isUploading}
                onUpload={fontManager.uploadFont}
                onDelete={fontManager.deleteFont}
            />
        </Box>
    );
};
